<template>
    <AppLayout>
        <div class="bg-white">
            <div class="pt-6">
                <nav aria-label="Breadcrumb">
                    <ol role="list"
                        class="mx-auto flex max-w-2xl items-center space-x-2 px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                        <li v-for="breadcrumb in page.breadcrumbs" :key="breadcrumb.id">
                            <div class="flex items-center">
                                <a :href="breadcrumb.href" class="mr-2 text-sm font-medium text-gray-900">{{
                                    breadcrumb.name
                                    }}</a>
                                <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor" aria-hidden="true"
                                    class="h-5 w-4 text-gray-300">
                                    <path d="M5.697 4.34L8.98 16.532h1.327L7.025 4.341H5.697z" />
                                </svg>
                            </div>
                        </li>
                        <li class="text-sm">
                            <a href="#" aria-current="page" class="font-medium text-gray-500 hover:text-gray-600">{{
                                page.name }}</a>
                        </li>
                    </ol>
                </nav>

                <!-- page info -->
                <div
                    class="mx-auto max-w-2xl px-4 pt-10 pb-16 sm:px-6 lg:grid lg:max-w-7xl lg:grid-cols-1 lg:grid-rows-[auto_auto_1fr] lg:gap-x-8 lg:px-8 lg:pt-16 lg:pb-24">
                    <div class="lg:col-span-2 lg:border-r lg:border-gray-200 lg:pr-8">
                        <h1 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{{ page.name }}</h1>
                    </div>

                    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div class="lg:col-span-2 max-md:order-1">
                            <p class="text-slate-500 text-sm mt-4">Complete your payment process to get the course.</p>

                            <div class="mt-6">
                                <div>
                                    <h3 class="text-lg font-semibold text-slate-900">30 Minute Plans</h3>
                                    <div class="grid gap-4 lg:grid-cols-2 mt-6">
                                        <div v-for="tier in tiers[0]?.tiers" :key="tier.id"
                                            class="bg-gray-100 p-4 rounded-md max-w-sm">
                                            <div>
                                                <div class="flex items-center">
                                                    <input type="radio" name="plan" class="w-5 h-5 cursor-pointer"
                                                        :id="`plan-${tier.id}`"
                                                        v-model="plan"
                                                        :value="tier"
                                                    />
                                                    <label :for="`plan-${tier.id}`" class="ml-4 flex gap-2 cursor-pointer">
                                                        <span class="text-slate-900 font-semibold">{{ tier.name }}</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <p class="mt-4 text-sm text-slate-500 font-medium">{{ tier.description }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-8">
                                    <h3 class="text-lg font-semibold text-slate-900">60 Minute Plans</h3>
                                    <div class="grid gap-4 lg:grid-cols-2 mt-6">
                                        <div v-for="tier in tiers[1]?.tiers" :key="tier.id"
                                            class="bg-gray-100 p-4 rounded-md max-w-sm">
                                            <div>
                                                <div class="flex items-center">
                                                    <input type="radio" name="method" class="w-5 h-5 cursor-pointer"
                                                        :id="`plan-${tier.id}`"
                                                        v-model="plan"
                                                        :value="tier"
                                                    />
                                                    <label :for="`plan-${tier.id}`" class="ml-4 flex gap-2 cursor-pointer">
                                                        <span class="text-slate-900 font-semibold">{{ tier.name }}</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <p class="mt-4 text-sm text-slate-500 font-medium">{{ tier.description }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-8 max-w-lg">
                                <h3 class="text-lg font-semibold text-slate-900">Choose your payment method</h3>
                                <div class="flex flex-wrap gap-4 justify-between mt-6">
                                    <div class="flex items-center">
                                        <input type="radio" class="w-5 h-5 cursor-pointer" id="paypal" checked />
                                        <label for="paypal" class="ml-4 flex gap-2 cursor-pointer">
                                            <img src="https://readymadeui.com/images/paypal.webp" class="w-20"
                                                alt="paypalCard" />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div v-if="plan" class="bg-gray-100 p-6 rounded-md">
                                <h2 class="text-2xl font-semibold text-slate-900">{{ plan ? plan.price : 0 }}</h2>
                                <ul class="text-slate-500 font-medium mt-8 space-y-4">
                                    <li class="flex flex-wrap gap-4 text-sm">{{ product.name }}
                                        <span class="ml-auto font-semibold text-slate-900">{{ plan ? plan.name : ''
                                            }}</span>
                                        <p>{{ plan ? plan.description : '' }}</p>
                                    </li>
                                    <li
                                        class="flex flex-wrap gap-4 text-[15px] font-semibold text-slate-900 border-t border-gray-300 pt-4">
                                        Total <span class="ml-auto">{{ plan ? plan.price : 0 }}</span></li>
                                </ul>
                                <Button
                                    label="Pay now"
                                    class="mt-6 w-full bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                                    @click="handlePayment"
                                    :disabled="!plan || loading"
                                >
                                    Pay now
                                </Button>
                                <p class="text-sm text-slate-500 mt-4">By clicking "Pay now", you agree to our terms and
                                    conditions.</p>
                            </div>
                            <div v-else class="bg-gray-100 p-6 rounded-md">
                                <p class="text-slate-500 font-medium">Please select a plan to proceed with the payment.</p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup>
import AppLayout from '@/components/layouts/AppLayout.vue';
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'primevue/button';
import { getProductBySlug } from '@/services/productService';
import { getTiers } from '@/services/tierService';
import { handlePaypalPayment } from '@/services/paymentService';

const route = useRoute();
const product = ref(null);
const tiers = ref({});
const plan = ref(null);
const loading = ref(false);

getTiers().then(data => {
    tiers.value = data.data;
});

if (route.params.slug) {
    getProductBySlug(route.params.slug).then(data => {
        product.value = data.data;
    });
}

const page = {
    name: 'Make a payment',
    breadcrumbs: [
        { id: 1, name: 'Home', href: '/' },
    ],
};

const handlePayment = () => {
    loading.value = true;
    if (!plan.value) {
        alert('Please select a plan to proceed with the payment.');
        loading.value = false;
        return;
    }
    
    handlePaypalPayment({
        product: product.value.slug,
        tier: plan.value.slug,
    }).then(response => {
        loading.value = false;
        // redirect to PayPal or handle the response accordingly
        if (response.approval_url) {
            window.location.href = response.approval_url;
        } else {
            alert('Payment initiation failed. Please try again.');
        }
    });
};

</script>